'use client';

import React, { useState, useEffect, useMemo } from 'react';
import { cn } from '@/lib/utils';

interface NestedSquaresLoaderProps {
  squareCount?: number;
  speed?: number;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
  colors?: string[];
}

export function NestedSquaresLoader({
  squareCount = 8,
  speed = 0.5,
  size = 'md',
  className,
  colors = ['#17c483', '#8f75f6', '#61c8f8', '#fd66a0', '#fdc557'],
}: NestedSquaresLoaderProps) {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
    
    const styleId = 'nested-squares-loader-styles';
    if (!document.getElementById(styleId)) {
      const style = document.createElement('style');
      style.id = styleId;
      style.textContent = `
        @keyframes nestedSquareMagic {
          0% {
            transform: scale(0) rotate(0deg);
            opacity: 0.8;
          }
          50% {
            transform: scale(1) rotate(90deg);
            opacity: 1;
          }
          100% {
            transform: scale(1.5) rotate(180deg);
            opacity: 0.6;
          }
        }

        .nested-square-item {
          animation: nestedSquareMagic calc(1.5s / var(--loader-speed, 1)) ease-in-out infinite alternate;
          animation-delay: var(--delay);
          transform-origin: center;
          will-change: transform, opacity;
        }
      `;
      document.head.appendChild(style);
    }
    
    return () => {
      // Don't remove styles on unmount as other instances may use them
    };
  }, []);

  const sizeMap = {
    sm: { container: 80, padding: 6 },
    md: { container: 120, padding: 8 },
    lg: { container: 160, padding: 10 },
  };

  const { container, padding } = sizeMap[size];

  const squares = useMemo(() => {
    return Array.from({ length: squareCount }, (_, i) => {
      const colorIndex = i % colors.length;
      return {
        id: i + 1,
        padding: (i + 1) * padding,
        offset: (i + 1) * -padding,
        color: colors[colorIndex],
        delay: i * 0.08,
      };
    });
  }, [squareCount, colors, padding]);

  if (!mounted) {
    return (
      <div 
        className={cn("flex items-center justify-center", className)}
        style={{ width: container, height: container }}
      />
    );
  }

  return (
    <div
      className={cn("relative flex items-center justify-center", className)}
      style={{ 
        width: container, 
        height: container,
        ['--loader-speed' as any]: speed,
      }}
    >
      {squares.map((square) => (
        <div
          key={square.id}
          className="nested-square-item absolute rounded-md border-2"
          style={{
            width: `calc(100% - ${square.padding * 2}px)`,
            height: `calc(100% - ${square.padding * 2}px)`,
            borderColor: square.color,
            boxShadow: `0 0 8px ${square.color}40, inset 0 0 4px ${square.color}20`,
            ['--delay' as any]: `${square.delay}s`,
          }}
        />
      ))}
    </div>
  );
}

export default NestedSquaresLoader;
